import { useState } from "react";
import { Icon } from "../../components/Icon";
import { Avatar, Btn, Card, Empty, PageHead, Skeleton, Tag } from "../../components/kit";
import { useMe, type Me } from "../../lib/api";
import { useMemories, useProfile, saveProfile, type StoredProfile } from "../../lib/hooks";

/* ---- reusable inline editors (no window.prompt) ---- */

function InlineAdd({ onAdd, placeholder, compact }: { onAdd: (v: string) => void; placeholder: string; compact?: boolean }) {
  const [draft, setDraft] = useState("");
  const commit = () => { const v = draft.trim(); if (v) onAdd(v); setDraft(""); };
  return (
    <div className={compact ? "inline-add compact" : "inline-add"}>
      <input
        className="inline-input"
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
      />
      {draft.trim() && <button className="inline-add-btn" onClick={commit}><Icon name="plus" size={13} strokeWidth={2.6} />Add</button>}
    </div>
  );
}

/** Short chip-style editable list (tech stack). */
function TagEditor({ items, onChange, placeholder }: { items: string[]; onChange: (next: string[]) => void; placeholder: string }) {
  return (
    <>
      <div className="tag-wrap">
        {items.map((t) => <Tag key={t} onRemove={() => onChange(items.filter((x) => x !== t))}>{t}</Tag>)}
        {items.length === 0 && <span className="muted-sm">Nothing yet.</span>}
      </div>
      <InlineAdd compact placeholder={placeholder} onAdd={(v) => { if (!items.includes(v)) onChange([...items, v]); }} />
    </>
  );
}

/** Sentence-style editable list (responsibilities, time sinks). */
function ListEditor({ items, onChange, placeholder, dotColor }: { items: string[]; onChange: (next: string[]) => void; placeholder: string; dotColor: string }) {
  return (
    <>
      {items.length === 0 ? (
        <div className="muted-sm" style={{ padding: "2px 0 8px" }}>Nothing yet — add the first below.</div>
      ) : (
        <ul className="resp-list">
          {items.map((r) => (
            <li key={r}>
              <Icon name="dot" size={16} color={dotColor} />{r}
              <button className="resp-x" title="Remove" onClick={() => onChange(items.filter((x) => x !== r))}><Icon name="x" size={12} /></button>
            </li>
          ))}
        </ul>
      )}
      <InlineAdd placeholder={placeholder} onAdd={(v) => { if (!items.includes(v)) onChange([...items, v]); }} />
    </>
  );
}

export function ProfilePage() {
  const me = useMe();
  const live = useProfile();

  return (
    <div className="page">
      <PageHead
        title="Profile & memory"
        sub="What Orbit knows about your work. The more accurate this is, the better every answer and lesson."
        actions={<Btn variant="ghost" icon="mic" onClick={() => window.alert("Re-run the voice interview from the Adaptr Orbit desktop app — it rebuilds this profile.")}>Re-run interview</Btn>}
      />
      {!me || !live ? <ProfileSkeleton /> : <Loaded me={me} profile={live} />}
    </div>
  );
}

function Loaded({ me, profile }: { me: NonNullable<Me>; profile: StoredProfile }) {
  const memory = useMemories();
  const [role, setRole] = useState(profile.role || "");
  const [stack, setStack] = useState<string[]>(profile.techStack);
  const [responsibilities, setResponsibilities] = useState<string[]>(profile.responsibilities);
  const [timeSinks, setTimeSinks] = useState<string[]>(profile.timeSinks);
  const [notes, setNotes] = useState<string>(profile.notes);

  const [editingRole, setEditingRole] = useState(false);
  const [roleDraft, setRoleDraft] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");

  // Always persist the full profile (current state + the field that changed).
  const save = (over: Partial<StoredProfile>) =>
    saveProfile({ role, responsibilities, techStack: stack, timeSinks, notes, ...over }).catch(() => {});

  const setStackP = (next: string[]) => { setStack(next); save({ techStack: next }); };
  const setRespP = (next: string[]) => { setResponsibilities(next); save({ responsibilities: next }); };
  const setSinksP = (next: string[]) => { setTimeSinks(next); save({ timeSinks: next }); };
  const saveRole = () => { const v = roleDraft.trim(); setRole(v); setEditingRole(false); save({ role: v }); };
  const saveNotes = () => { setNotes(notesDraft); setEditingNotes(false); save({ notes: notesDraft }); };

  const roleLabel = (role || me.role || "").replace(/^\w/, (c) => c.toUpperCase());

  return (
    <div className="two-col">
      <div className="stack-lg">
        <Card
          title="Identity"
          icon="id-card"
          action={!editingRole ? <button className="icon-mini" title="Edit role" onClick={() => { setRoleDraft(role); setEditingRole(true); }}><Icon name="pencil" size={14} /></button> : undefined}
        >
          <div className="id-row">
            <Avatar initials={me.initials} size={56} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="id-name">{me.name}</div>
              {editingRole ? (
                <div className="inline-add" style={{ margin: "6px 0" }}>
                  <input className="inline-input" value={roleDraft} autoFocus placeholder="e.g. Senior Product Designer"
                    onChange={(e) => setRoleDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveRole(); if (e.key === "Escape") setEditingRole(false); }} />
                  <Btn variant="live" size="sm" icon="check" onClick={saveRole}>Save</Btn>
                </div>
              ) : (
                <div className="id-role">{roleLabel || "Add your role"}{me.org?.name ? ` · ${me.org.name}` : ""}</div>
              )}
              <div className="id-mail">{me.email}</div>
            </div>
          </div>
        </Card>

        <Card title="Responsibilities" icon="briefcase">
          <ListEditor items={responsibilities} onChange={setRespP} placeholder="Add a responsibility…" dotColor="var(--live-dim)" />
        </Card>

        <Card title="Tech stack" icon="layers">
          <TagEditor items={stack} onChange={setStackP} placeholder="Add a tool…" />
        </Card>

        <Card title="Notes" icon="sticky-note" action={!editingNotes ? <button className="icon-mini" onClick={() => { setNotesDraft(notes); setEditingNotes(true); }}><Icon name="pencil" size={14} /></button> : undefined}>
          {editingNotes ? (
            <div>
              <textarea className="notes-edit" value={notesDraft} autoFocus onChange={(e) => setNotesDraft(e.target.value)} placeholder="Anything that helps Orbit help you…" />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                <Btn variant="ghost" size="sm" onClick={() => setEditingNotes(false)}>Cancel</Btn>
                <Btn variant="live" size="sm" icon="check" onClick={saveNotes}>Save</Btn>
              </div>
            </div>
          ) : (
            <p className="notes-text" onClick={() => { setNotesDraft(notes); setEditingNotes(true); }} style={{ cursor: "text" }}>
              {notes || "No notes yet — click to add anything Orbit should keep in mind."}
            </p>
          )}
        </Card>
      </div>

      <div className="stack-lg">
        <Card title="Where your time goes" icon="clock">
          <ListEditor items={timeSinks} onChange={setSinksP} placeholder="Add a time-sink…" dotColor="var(--violet-500)" />
        </Card>

        <Card title="What Orbit remembers" icon="brain-circuit" iconColor="var(--violet-500)" count={memory?.items.length}>
          {memory === null ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={18} />)}</div>
          ) : memory.items.length === 0 ? (
            <Empty icon="brain-circuit">Nothing remembered yet — as you work, Orbit learns what matters to you.</Empty>
          ) : (
            <div className="mem-list">
              {memory.items.map((m) => (
                <div className="mem" key={m.id}>
                  <span className="mem-ic"><Icon name={m.ic} size={14} /></span>
                  <span className="mem-t">{m.t}</span>
                  <button className="mem-forget" title="Forget" onClick={() => memory.forget(m.id)}>Forget</button>
                </div>
              ))}
            </div>
          )}
          <div className="mem-foot">
            <Icon name="shield" size={13} />
            <span>Stored on your account. Review and forget anything, anytime.</span>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="two-col">
      <div className="stack-lg">
        <Card title="Identity" icon="id-card">
          <div className="id-row">
            <Skeleton w={56} h={56} r={28} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              <Skeleton w={160} h={18} /><Skeleton w={200} h={13} /><Skeleton w={180} h={13} />
            </div>
          </div>
        </Card>
        <Card title="Responsibilities" icon="briefcase">
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} h={16} />)}</div>
        </Card>
        <Card title="Tech stack" icon="layers">
          <div className="tag-wrap">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} w={72} h={28} r={8} />)}</div>
        </Card>
      </div>
      <div className="stack-lg">
        <Card title="Where your time goes" icon="clock"><div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} h={16} />)}</div></Card>
        <Card title="What Orbit remembers" icon="brain-circuit"><div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={18} />)}</div></Card>
      </div>
    </div>
  );
}
